import { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import type { ApiClient, Sanitaeruebersicht } from '../api';
import { Fachaktionen } from './Fachaktionen';
import { GewerkUnterlagenZip } from '../UnterlagenZipKnopf';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, LoadingState,
  StatusBadge, WertEinheit, formatiereZahl, type Spalte,
} from '../muster';
import { zeitpunkt } from './sanitaer/kopf';

/**
 * Das Gewerk Sanitär.
 *
 * --- Was hier steht und was nicht ---------------------------------------------
 *
 * Der Bestand entsteht aus EINEM Lauf: 19.921 Rohre, 19.040 Formteile, 635
 * Zubehör, 285 Abläufe, 310 HLS-Geräte — und dazu die Sanitärgegenstände aus
 * dem Architektur- und dem Küchenmodell, die nur über die Verknüpfung sichtbar
 * sind.
 *
 * --- Warum die Lückenzahl in den Kennzahlen steht -----------------------------
 *
 * Beim Abwasser ist ein offenes Rohrende an drei Stellen richtig und überall
 * sonst eine Lücke, die das Netz zerteilt. Ein zerteiltes Netz lässt eine
 * Berechnung einen Teil erfassen, ohne zu sagen, dass sie es tut — deshalb ist
 * die Lückenzahl die erste Zahl, die jemand sehen muss, und deshalb steht sie
 * als Kennzahl hier auf der Übersicht. Die Liste der einzelnen Lücken steht
 * unter `sanitaer/befunde` — seit S1 eine eigene Route (`art: pruefung`) wie
 * `lueftung/befunde`, nicht mehr ein Reiter dieser Seite.
 *
 * --- Warum leere Felder unterschieden werden ----------------------------------
 *
 * Ein Rohr ohne Gefälle ist bei einer Fallleitung richtig und bei einer
 * Sammelleitung ein Befund. Beides als „leer" zu zeigen, macht die Liste
 * unbrauchbar — bei den Elektroverteilern stand einmal „kein Bereich" bei einem
 * Trafo, der keinen haben soll.
 */

interface Rahmen { api: ApiClient; projekt?: { id: string } }

const KREISNAMEN: Record<string, string> = {
  trinkwasser_kalt: 'Trinkwasser kalt',
  trinkwasser_warm: 'Trinkwasser warm',
  zirkulation: 'Zirkulation',
  schmutzwasser: 'Schmutzwasser',
  schmutzwasser_grund: 'Schmutzwasser-Grundleitung',
  entlueftung: 'Entlüftung',
  kondensat: 'Kondensat',
  regenwasser: 'Regenwasser',
  notentwaesserung: 'Notentwässerung',
  druckleitung: 'Druckleitung',
  fettabwasser: 'Fettabwasser',
};

export function Sanitaer() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [u, setU] = useState<Sanitaeruebersicht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!projekt) return;

    api.sanitaerUebersicht(projekt.id)
      .then((x) => { setU(x); setFehler(null); })
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projekt?.id]);

  if (!projekt) return <LoadingState text="Projekt wird abgerufen …" />;

  // `!u.gesamt` und nicht `u.gesamt === 0`: Eine Antwort ohne `gesamt` ist
  // ebenfalls ein leerer Bestand. Mit dem Vergleich auf 0 lief die Seite
  // weiter und brach in `u.jeKreis.filter` ab (gemessen im Prüfstand, U6b).
  const leer = !u || !u.gesamt;

  return (
    <>
      <PageHeader
        titel="Sanitär"
        beschreibung={'Rohrnetz, Sanitärgegenstände und die Bemessung je Bereich. '
          + 'Der Bestand entsteht aus EINEM Lauf über das Sanitärmodell und die '
          + 'verknüpften Architektur- und Küchenmodelle.'}
        meta={u?.gelesen
          ? `Zuletzt gelesen ${zeitpunkt(u.gelesen)}`
          : undefined}
      />

      <Fachaktionen
        api={api}
        rolleKey="sanitaer"
        leseArt={80}
        leseText="Modell lesen"
      >
        <GewerkUnterlagenZip api={api} projektId={projekt.id} gewerk="sanitaer" />
      </Fachaktionen>

      {fehler && (
        <ErrorState
          meldung="Die Sanitärübersicht konnte nicht geladen werden."
          technisch={fehler}
        />
      )}

      {leer ? (
        <EmptyState
          titel="Für dieses Modell ist noch kein Sanitärbestand aufgenommen."
          erklaerung={(
            <>
            Er entsteht aus einem Auftrag mit der Sanitäraufnahme — oben
            „Modell lesen“.
            <span style={{ display: 'block', marginTop: 12 }}>
              Der Lauf liest das Rohrnetz des Sanitärmodells <em>und</em> die
              Sanitärgegenstände aus den verknüpften Modellen. WCs, Waschtische
              und Küchengeräte liegen nämlich nicht im Sanitärmodell — die
              Sanitärinstallationsliste enthält nur Dach- und Bodenabläufe. Ohne
              die Verknüpfungen gäbe es keine Anschlusswerte und damit keinen
              Summendurchfluss.
            </span>
            <span className="schwach" style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
              Erwartet werden rund 38.900 Elemente in elf Kreisen. Die
              Verknüpfungen zum Architektur- und Küchenmodell müssen GELADEN
              sein — eine entladene Verknüpfung liefert null Gegenstände, und
              das sieht aus wie ein Modell ohne WCs.
            </span>
            </>
          )}
        />
      ) : (
        <>
          <Rechenknopf api={api} projektId={projekt.id} />
          <Kennzahlen u={u!} />
          <Kreise u={u!} />
          <Gegenstaende u={u!} />
          <Annahmen u={u!} />
        </>
      )}
    </>
  );
}

/**
 * Alle fünf Bereiche rechnen.
 *
 * Ein Knopf und nicht fünf: Fünf Aufrufe hießen fünf Reihenfolgen, die jemand
 * kennen muss — und beim sechsten Mal kennt er sie nicht mehr.
 *
 * Ein Bereich, der scheitert, nimmt die übrigen vier nicht mit. Was scheitert,
 * wird als Befund gezeigt und nicht als Absturz.
 */
function Rechenknopf({ api, projektId }: {
  api: ApiClient; projektId: string;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<
    Awaited<ReturnType<ApiClient['sanitaerRechnen']>> | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const rechnen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      setErgebnis(await api.sanitaerRechnen(projektId));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  const bereichspalten: Spalte<any>[] = [
    { schluessel: 'bereich', titel: 'Bereich', immerSichtbar: true, wert: (b) => b.bereich },
    { schluessel: 'zeilen', titel: 'Zeilen', art: 'zahl', wert: (b) => b.zeilen },
    {
      schluessel: 'beanstandet', titel: 'beanstandet', art: 'zahl',
      wert: (b) => b.beanstandet,
      inhalt: (b) => (b.beanstandet > 0
        ? <StatusBadge zustand="warnung" text={String(b.beanstandet)} klein />
        : <>0</>),
    },
    {
      schluessel: 'stand', titel: 'Stand', art: 'status', breite: '220px',
      wert: (b) => (b.fehler ?? 'gespeichert'),
      inhalt: (b) => (b.fehler
        ? <StatusBadge zustand="fehler" text={b.fehler} klein />
        : <StatusBadge zustand="gut" text="gespeichert" klein />),
    },
  ];

  return (
    <ContentSection
      titel="Rohrnetzberechnung"
      beschreibung="Ein Knopf für alle Bereiche: Fünf Aufrufe hießen fünf Reihenfolgen, die jemand kennen muss — und beim sechsten Mal kennt er sie nicht mehr."
      aktionen={(
        <button className="m-knopf m-knopf-haupt" onClick={rechnen} disabled={laeuft}>
          {laeuft ? 'rechnet …' : 'Alle Bereiche rechnen'}
        </button>
      )}
    >
      {fehler && (
        <ErrorState
          klein
          meldung="Die Rohrnetzberechnung ist gescheitert."
          technisch={fehler}
        />
      )}

      {ergebnis && (
        <>
          <DataTable
            name="Bereiche"
            spalten={bereichspalten}
            zeilen={ergebnis.bereiche}
            schluessel={(b) => String(b.bereich)}
            suche={false}
            spaltenwahl={false}
            dicht
          />

          {ergebnis.offenePunkte.length > 0 && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: 'pointer', fontSize: 13 }}>
                {ergebnis.offenePunkte.length} offene Punkte
              </summary>
              <ul style={{ fontSize: 12, lineHeight: 1.7 }}>
                {ergebnis.offenePunkte.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </details>
          )}
        </>
      )}
    </ContentSection>
  );
}

function Kennzahlen({ u }: { u: Sanitaeruebersicht }) {
  const navigate = useNavigate();
  const kreise = (u.jeKreis ?? []).filter((k) => k.kreis).length;
  const ohneKreis = (u.jeSystemtypOhneKreis ?? []).reduce((s, x) => s + x.anzahl, 0);
  const gegenstaende = (u.gegenstaende ?? []).reduce((s, x) => s + x.anzahl, 0);
  // Die Lückenzahl ist die erste Zahl, die jemand sehen muss (siehe
  // Kopfkommentar): Ein zerteiltes Netz lässt eine Berechnung nur einen Teil
  // erfassen, ohne zu sagen, dass sie es tut. Die Karte steht deshalb schon
  // in den Kennzahlen der Übersicht und führt zur Befundseite, auf der die
  // Lücken einzeln stehen.
  const luecken = (u.offene ?? []).find((o) => o.einstufung === 'luecke')?.anzahl ?? 0;

  return (
    <KpiReihe>
      <KpiCard titel="Elemente" wert={u.gesamt} />
      <KpiCard titel="Verbindungen" wert={u.verbindungen} />
      <KpiCard titel="Kreise" wert={kreise} />
      <KpiCard
        titel="Gegenstände" wert={gegenstaende}
        vergleich="aus den verknüpften Modellen"
      />
      {luecken > 0 && (
        <KpiCard
          titel="Lücken im Netz" wert={luecken}
          zustand="warnung"
          zustandText="zerteilt das Netz — Details unter Befunde"
          beiKlick={() => navigate('befunde')}
        />
      )}
      {ohneKreis > 0 && (
        <KpiCard
          titel="Rohre ohne Kreis" wert={ohneKreis}
          zustand="warnung"
          zustandText="kein Systemtyp trifft eine Kreisregel"
        />
      )}
      {u.gelesen && (
        <KpiCard
          titel="Zuletzt gelesen"
          wert={new Date(u.gelesen).toLocaleDateString('de-DE')}
        />
      )}
    </KpiReihe>
  );
}

function Kreise({ u }: { u: Sanitaeruebersicht }) {
  const spalten: Spalte<any>[] = [
    {
      schluessel: 'kreis', titel: 'Kreis', immerSichtbar: true,
      wert: (k) => (k.kreis ? KREISNAMEN[k.kreis] ?? k.kreis : 'ohne Kreis'),
      inhalt: (k) => (k.kreis
        ? <strong>{KREISNAMEN[k.kreis] ?? k.kreis}</strong>
        : <em className="schwach">ohne Kreis</em>),
    },
    {
      schluessel: 'fuehrung', titel: 'Führung', wert: (k) => k.fuehrungsart,
      inhalt: (k) => <span className="schwach">{k.fuehrungsart ?? '—'}</span>,
    },
    { schluessel: 'rohre', titel: 'Rohre', art: 'zahl', wert: (k) => k.rohre },
    { schluessel: 'laenge', titel: 'Länge', art: 'zahl', einheit: 'm', wert: (k) => k.laengeM },
    {
      // Eine Fallleitung HAT kein Gefälle - das ist richtig und deshalb eine
      // eigene Spalte, nicht ein Mangel.
      schluessel: 'fallleitungen', titel: 'Fallleitungen', art: 'zahl',
      titelHinweis: 'senkrecht — dort gibt es kein Gefälle, und das ist richtig',
      wert: (k) => k.fallleitungen,
    },
    {
      schluessel: 'ohneGefaelle', titel: 'ohne Gefälle', art: 'zahl',
      titelHinweis: 'waagerecht ohne ermittelbares Gefälle — meist fehlt die Lage',
      wert: (k) => k.ohneGefaelle,
      inhalt: (k) => (k.ohneGefaelle > 0
        ? <StatusBadge zustand="warnung" text={formatiereZahl(k.ohneGefaelle)} klein />
        : <span className="schwach">—</span>),
    },
    {
      schluessel: 'gegengefaelle', titel: 'Gegengefälle', art: 'zahl',
      titelHinweis: 'steigt in Zeichenrichtung — vor der Netzdurchmusterung noch kein Befund',
      wert: (k) => k.gegengefaelle,
      inhalt: (k) => (k.gegengefaelle > 0
        ? <>{formatiereZahl(k.gegengefaelle)}</>
        : <span className="schwach">—</span>),
    },
  ];

  return (
    <ContentSection
      titel="Kreise"
      beschreibung={'„Gegengefälle“ ist vor der Netzdurchmusterung noch kein Befund: '
        + 'Die Bezugsrichtung ist die Zeichenrichtung des Rohres und damit '
        + 'willkürlich. Erst wenn die Fließrichtung feststeht, ist ein negatives '
        + 'Gefälle ein Mangel.'}
      nackt
    >
      <DataTable
        name="Kreise"
        spalten={spalten}
        zeilen={u.jeKreis ?? []}
        schluessel={(k) => String(k.kreis ?? 'ohne')}
        ausfuhr
        ausfuhrDateiname="sanitaer-kreise"
        leer={(
          <EmptyState
            titel="Kein Kreis im Bestand."
            erklaerung="Die Kreise entstehen aus den Systemtypen des Modells über die Kreisregeln."
          />
        )}
      />

      {(u.jeSystemtypOhneKreis ?? []).length > 0 && (
        <div className="hinweis warnung" style={{ display: 'block', marginTop: 12 }}>
          <span className="hinweis-zeichen" style={{ display: 'block' }}>!</span>
          <div>
            Systemtypen ohne Kreisregel:{' '}
            {u.jeSystemtypOhneKreis
              .map((x) => `${x.systemtyp ?? 'ohne Systemtyp'} (${x.anzahl})`)
              .join(', ')}. Diese Rohre werden von keiner Bemessung erfasst. Ein
            geratener Kreis wäre eine Bemessung nach der falschen Norm — die
            Regel gehört unter <em>Normwerte</em> ergänzt.
          </div>
        </div>
      )}
    </ContentSection>
  );
}

const ARTNAMEN: Record<string, string> = {
  wc: 'WC',
  urinal: 'Urinal',
  urinaltrennwand: 'Urinaltrennwand',
  waschtisch: 'Waschtisch',
  dusche: 'Dusche',
  badewanne: 'Badewanne',
  spuele: 'Spüle',
  spuelmaschine: 'Spülmaschine',
  ausgussbecken: 'Ausgussbecken',
  bodenablauf: 'Bodenablauf',
  dachablauf: 'Dachablauf',
  kombidaempfer: 'Kombidämpfer',
  ohne_wasser: 'ohne Wasseranschluss',
  sonstiges: 'noch nicht zugeordnet',
};

/**
 * Die Sanitärgegenstände aus den verknüpften Modellen.
 *
 * Die Spalte „ausgeschlossen" ist der Grund, warum diese Tabelle so aussieht:
 * Der erste Lauf lieferte 5.574 Gegenstände, davon 1.891 aus den EIGENEN
 * Fachmodellen — Ventilatorkonvektoren, Heizkörper, Freiraumkörper. Eine
 * Tabelle, die nur die Gesamtzahl je Modell zeigt, hätte das verdeckt: 1.147
 * Gegenstände aus dem Heizungsmodell sehen darin aus wie ein reich
 * ausgestattetes Sanitärgewerk.
 */
function Gegenstaende({ u }: { u: Sanitaeruebersicht }) {
  const sanitaer = (u.gegenstaende ?? []).reduce((s, x) => s + x.sanitaer, 0);
  const aus = (u.gegenstaende ?? []).reduce((s, x) => s + x.ausgeschlossen, 0);

  const modellspalten: Spalte<any>[] = [
    { schluessel: 'modell', titel: 'Herkunftsmodell', immerSichtbar: true, wert: (g) => g.herkunftsmodell },
    {
      schluessel: 'sanitaer', titel: 'Sanitär', art: 'zahl', wert: (g) => g.sanitaer,
      inhalt: (g) => (g.sanitaer > 0
        ? <strong>{formatiereZahl(g.sanitaer)}</strong>
        : <span className="schwach">—</span>),
    },
    {
      schluessel: 'ausgeschlossen', titel: 'ausgeschlossen', art: 'zahl',
      titelHinweis: 'aus einem eigenen Fachmodell, oder Schreinerarbeit ohne Rohranschluss',
      wert: (g) => g.ausgeschlossen,
    },
    { schluessel: 'mitRaum', titel: 'mit Raum', art: 'zahl', wert: (g) => g.mitRaum },
  ];

  const artspalten: Spalte<any>[] = [
    {
      schluessel: 'art', titel: 'Art', immerSichtbar: true,
      wert: (a) => (ARTNAMEN[a.art ?? ''] ?? a.art ?? '—'),
      inhalt: (a) => (a.art === 'sonstiges'
        ? <em>{ARTNAMEN.sonstiges}</em>
        : <>{ARTNAMEN[a.art ?? ''] ?? a.art ?? '—'}</>),
    },
    { schluessel: 'anzahl', titel: 'Anzahl', art: 'zahl', wert: (a) => a.anzahl },
    {
      // Drei verschiedene Bedeutungen von leer, und sie zu vermischen macht
      // die Liste unbrauchbar.
      schluessel: 'du', titel: 'Anschlusswert', einheit: 'DU', ausrichtung: 'rechts',
      wert: (a) => a.mitAnschlusswert,
      inhalt: (a) => (a.art === 'ohne_wasser'
        ? <StatusBadge zustand="neutral" text="kein Wasseranschluss" klein titel="trifft nicht zu" />
        : a.mitAnschlusswert > 0
          ? <WertEinheit wert={a.mitAnschlusswert} rechts />
          : <StatusBadge zustand="warnung" text="noch nicht gepflegt" klein />),
    },
  ];

  return (
    <ContentSection
      titel="Sanitärgegenstände aus verknüpften Modellen"
      beschreibung={'Die Lage ist mit GetTotalTransform ins Projektsystem gebracht. '
        + 'Ohne diese Umrechnung lägen die Koordinaten im System der Verknüpfung — '
        + 'sie sähen plausibel aus und wären systematisch falsch.'}
    >
      {sanitaer === 0 ? (
        <EmptyState
          titel="Kein einziger Sanitärgegenstand."
          erklaerung={'Ohne WCs, Waschtische und Küchengeräte gibt es keine '
            + 'Anschlusswerte und damit keinen Summendurchfluss — die '
            + 'Abwasserbemessung kann nicht anlaufen. Fast immer ist die Ursache '
            + 'eine entladene Verknüpfung und nicht ein Modell ohne WCs.'}
        />
      ) : (
        <>
          <DataTable
            name="Gegenstände je Herkunftsmodell"
            spalten={modellspalten}
            zeilen={u.gegenstaende ?? []}
            schluessel={(g) => String(g.herkunftsmodell ?? 'ohne')}
            suche={false}
            spaltenwahl={false}
            dicht
          />

          <p className="schwach" style={{ fontSize: 12 }}>
            {formatiereZahl(aus)} Gegenstand/Gegenstände ausgeschlossen —
            sie stammen aus den eigenen Fachmodellen (Heizung, Lüftung, Elektro,
            Sprinkler, Außenanlagen) oder sind Schreinerarbeiten ohne
            Rohranschluss. Sie werden nicht gelöscht, sondern tragen ihren Grund;
            wer die Regel anzweifelt, sieht nach, was sie weggenommen hat.
          </p>

          {(u.gegenstandsarten ?? []).length > 0 && (
            <DataTable
              name="Gegenstände je Art"
              spalten={artspalten}
              zeilen={u.gegenstandsarten}
              schluessel={(a) => String(a.art ?? 'ohne')}
              ausfuhr
              ausfuhrDateiname="sanitaer-gegenstandsarten"
              dicht
            />
          )}
        </>
      )}
    </ContentSection>
  );
}

/**
 * Die Annahmen.
 *
 * Sie stehen sichtbar auf der Seite und nicht nur in der Datenbank: Der
 * Grenzabstand von 300 mm ist ausdrücklich kein Normwert, und wer eine
 * Einstufung anzweifelt, muss lesen können, mit welchem Wert sie entstanden ist.
 */
function Annahmen({ u }: { u: Sanitaeruebersicht }) {
  if ((u.annahmen ?? []).length === 0) return null;

  const spalten: Spalte<any>[] = [
    { schluessel: 'bezeichnung', titel: 'Annahme', immerSichtbar: true, wert: (a) => a.bezeichnung },
    {
      schluessel: 'wert', titel: 'Wert', ausrichtung: 'rechts', wert: (a) => a.wert,
      inhalt: (a) => (a.wert === null
        ? <StatusBadge zustand="warnung" text="nicht gepflegt" klein />
        : <WertEinheit wert={a.wert} einheit={a.einheit ?? undefined} rechts />),
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '200px',
      wert: (a) => `${a.herkunft}${a.geprueft ? ' · geprüft' : ' · ungeprüft'}`,
      inhalt: (a) => (a.geprueft
        ? <StatusBadge zustand="gut" text={`${a.herkunft} · geprüft`} klein />
        : <StatusBadge zustand="warnung" text={`${a.herkunft} · ungeprüft`} klein />),
    },
    {
      schluessel: 'begruendung', titel: 'Begründung', wert: (a) => a.begruendung,
      inhalt: (a) => (
        <span className="schwach" style={{ fontSize: 12 }}>{a.begruendung ?? ''}</span>
      ),
    },
  ];

  return (
    <ContentSection
      titel="Annahmen"
      beschreibung={'Sie stehen sichtbar auf der Seite und nicht nur in der '
        + 'Datenbank: Der Grenzabstand von 300 mm ist ausdrücklich kein Normwert, '
        + 'und wer eine Einstufung anzweifelt, muss lesen können, mit welchem Wert '
        + 'sie entstanden ist.'}
      nackt
    >
      <DataTable
        name="Annahmen"
        spalten={spalten}
        zeilen={u.annahmen}
        schluessel={(a) => String(a.schluessel)}
        ausfuhr
        ausfuhrDateiname="sanitaer-annahmen"
        dicht
      />
    </ContentSection>
  );
}
